import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useContext, useState } from 'react';

import { useCreateApplicationNote } from '@features/applicationNotes/hooks/useCreateApplicationNote';
import { useCreateEnterpriseApplicationNote } from '@features/applicationNotes/hooks/useCreateEnterpriseApplicationNote';
import CloseIcon from '@mui/icons-material/Close';
import { AddNoteIcon, DateIcon } from '../../../../vectors/index';
import { useGetEnterpriseSavedMessages } from '@features/savedMessages/hooks/useGetEnterpriseSavedMessages';
import { authContext } from '@staffedupusa/shared-components';
import { toast } from 'react-toastify';

type CreateEnterpriseNoteModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  applicationId: number;
  companyId: number;
};

const CreateEnterpriseNoteModal: FC<CreateEnterpriseNoteModalProps> = ({
  isOpen,
  handleClose,
  applicationId,
  companyId
}) => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState('');
  const [name, setName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [option, setOption] = useState('template');

  const { createNote, isCreateNoteLoading } = useCreateEnterpriseApplicationNote({
    company_id: companyId,
    setErrorText,
  });
  const { savedEnterpriseMessages } = useGetEnterpriseSavedMessages({ enabled: authData?.user?.enterprise && companyId ? true : false, company_id: companyId, isNote: true });
  const handleChangeTemplate = (event: { target: { value: string }; }) => {
    setSelectedTemplate(event.target.value);
  };

  const handleCreateNew = () => {
    if (option === 'template' && selectedTemplate) {
      const selectedMessage = savedEnterpriseMessages?.object?.find(
        (msg) => msg.id.toString() === selectedTemplate
      );
      if (selectedMessage) {
        createNote(
          {
            application_id: applicationId,
            name: selectedMessage.label,
            note: selectedMessage.content,
            company_id: companyId
          },
          {
            onSuccess: () => {
              toast.success('Note added successfully')
              setSelectedTemplate('');
              handleClose();
            }
          }
        );
      }
    } else {
      createNote(
        { application_id: applicationId, company_id: companyId, note: '', name },
        {
          onSuccess: () => {
            toast.success('Note added successfully')
            handleClose();
            setName('');
          },
        }
      );
    }
  }
  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <AddNoteIcon />
        <br />
        <Typography variant="h4">Create a Note</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ marginBottom: 4 }} spacing={2}>
          {errorText && <Alert severity="error">{errorText}</Alert>}

          <RadioGroup
            value={option}
            onChange={(e) => setOption(e.target.value)}
            row
          >
            <FormControlLabel
              value="template"
              control={<Radio />}
              label="Create from an existing template"
            />
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="Create new"
            />
          </RadioGroup>

          {option === 'template' ? (
            <Select
              value={selectedTemplate}
              onChange={handleChangeTemplate}
              displayEmpty
              variant="outlined"
              size="small"
              fullWidth
              MenuProps={{
                disablePortal: true,
              }}
            >
              <MenuItem value="" disabled>
                Select template
              </MenuItem>
              {savedEnterpriseMessages?.object?.map((message) => (
                <MenuItem key={message.id} value={message.id.toString()}>
                  {message.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              value={name}
              size="small"
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              inputProps={{ maxLength: 100 }}
              fullWidth
            />
          )}

          <LoadingButton
            variant="contained"
            onClick={handleCreateNew}
            disabled={option === 'new' && !name}
            loading={isCreateNoteLoading}
            sx={{ fontSize: '16px', marginTop: '10px !imporant' }}
          >
            Create Note
          </LoadingButton>

          <Button
            sx={{ fontSize: '16px', marginTop: '10px !imporant' }}
            variant="outlined"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEnterpriseNoteModal;
